import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Statistic, Layout, Input, Button, Flex, message, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    QuestionCircleOutlined,
    InfoCircleOutlined,
    DatabaseOutlined,
    CalendarOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { updateKnowledgeDatabase } from '../../KnowledgeDatabaseServices';
import { resolveBackendMessage } from '../../../../Utils/apiMessages';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import { formatShortDate } from '../../../../Utils/date';

const { Title, Text } = Typography;
const { Content } = Layout;

const DatabaseKPIs = ({ databaseInfo = {}, refresh}) => {
    const { t, i18n } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [editedDescription, setEditedDescription] = useState('');
    const [updating, setUpdating] = useState(false);
    const { handleError } = useErrorHandler();

    useEffect(() => {
        if (databaseInfo) {
            setEditedName(databaseInfo.name);
            setEditedDescription(databaseInfo.description);
        }
    }, [databaseInfo]);

    const onUpdate = useCallback(async (data) => {
        try {
            setUpdating(true);
            const payload = await updateKnowledgeDatabase(data);
            refresh();
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
        } catch (error) {
            handleError(error);
        } finally {
            setUpdating(false);
        }
    }, [handleError, refresh, t]);

    if (!databaseInfo) return null;

    const handleSave = () => {
        onUpdate({
            name: editedName,
            description: editedDescription,
            id: databaseInfo.id,
        });
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditedName(databaseInfo.name);
        setEditedDescription(databaseInfo.description);
        setIsEditing(false);
    };
    const mainCardProps = {
        bordered: false,
        size: 'small',
        style: {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            borderRadius: '6px',
            padding: 0,
        }
    };

    const kpiConfigs = [
        {
            title: t('qa_entries'),
            value: databaseInfo.qa_count,
            icon: <QuestionCircleOutlined />,
            color: '#1890ff',
            tooltip: t('total_qa_pairs')
        },
        {
            title: t('business_info'),
            value: databaseInfo.business_info_count,
            icon: <InfoCircleOutlined />,
            color: '#52c41a',
            tooltip: t('business_entries')
        },
        {
            title: t('total_entries'),
            value: databaseInfo.entry_count,
            icon: <DatabaseOutlined />,
            color: '#531dab',
            tooltip: t('all_entries')
        },
        {
            title: t('created'),
            value: formatShortDate(databaseInfo.created_at, i18n.language),
            icon: <CalendarOutlined />,
            color: '#666666',
            tooltip: t('creation_date')
        }
    ];

    return (
        <Layout>
            <Content>
                <Card {...mainCardProps}>
                    <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 8 }}>
                        <Col span={24}>
                            {isEditing ? (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Input
                                        placeholder={t('name')}
                                        value={editedName}
                                        size='medium'
                                        onChange={(e) => setEditedName(e.target.value)}
                                        style={{ fontWeight: 600 }}
                                    />
                                    <Flex gap={8} align="middle">
                                        <Input
                                            placeholder={t('description')}
                                            size='medium'
                                            value={editedDescription}
                                            onChange={(e) => setEditedDescription(e.target.value)}
                                            style={{ flex: 1 }}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<SaveOutlined />}
                                            onClick={handleSave}
                                            loading={updating}
                                            shape="circle"
                                            style={{
                                                backgroundColor: '#1890ff',
                                                borderColor: '#1890ff'
                                            }}
                                        />
                                        <Button
                                            type="default"
                                            icon={<CloseOutlined />}
                                            onClick={handleCancel}
                                            shape="circle"
                                            style={{
                                                color: '#1890ff',
                                                borderColor: '#1890ff'
                                            }}
                                        />
                                    </Flex>
                                </Space>
                            ) : (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Flex gap={8} align='flex-end'>
                                        <Title level={3} style={{ margin: 0 }}>
                                            {databaseInfo.name}
                                        </Title>
                                        <Button
                                            type="text"
                                            size='small'
                                            icon={<EditOutlined />}
                                            onClick={() => setIsEditing(true)}
                                            style={{
                                                borderRadius: '50%',
                                                color: '#1890ff',
                                                border: '1px solid #1890ff'
                                            }}
                                        />
                                    </Flex>
                                    <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px' }}>
                                        {databaseInfo.description}
                                    </Text>
                                </Space>
                            )}
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        {kpiConfigs.map((kpi, index) => (
                            <Col xs={12} sm={12} md={6} key={index}>
                                <Statistic
                                    title={<Text type="secondary" style={{ fontSize: '13px' }}>{kpi.title}</Text>}
                                    value={kpi.value}
                                    valueStyle={{ color: kpi.color, fontSize: '18px' }}
                                    prefix={React.cloneElement(kpi.icon, {
                                        style: { color: kpi.color, fontSize: '14px' }
                                    })}
                                />
                            </Col>
                        ))}
                    </Row>
                </Card>
            </Content>
        </Layout>
    );
};

export default DatabaseKPIs;
